// API Models

import { ProductsUserData } from '../store/order-wizard/order-wizard-data/order-wizard-data.model';
import { DatesOfService } from './dates-of-service.model';
import { ReorderDetailsItem } from './reorder.model';

export interface ApiCopayRequestItem {
  id: number;
  cashSale: boolean;
  shipmentType: number;
  quantity: number;
  shipmentDate: string;
  daysBetweenShipments: number;
  // These fields are submitted by iPIMS but do not exist in the API swagger model
  // http://ccsdc-appsupp/swagger/?url=https://pwsdev.ccsmed.com/items-api-dev/api/swagger.json#/default/post_copay
  ship: boolean;
  charge: boolean | null;
  duplicate: boolean;
}

export interface ApiCopayRequest {
  patientId: number;
  deductibleMet: boolean;
  items: ApiCopayRequestItem[];
  useExclusionList?: boolean;
  overriddenPrimaryRemainingDeductible?: 0;
  overriddenSecondaryRemainingDeductible?: 0;
}

export interface ApiCopayItemResponse {
  item: {
    itemId: number;
    quantity: number;
    description: string;
  };
}

export interface ApiCopayDetailsItemResponse {
  item: {
    itemId: number;
  };
  total: number;
  patientAmount: number;
  primaryInsuranceAmount: number;
  secondaryInsuranceAmount: number;
  primaryReimbursementPercentage: number;
  secondaryReimbursementPercentage: number;
  remainingDeductibleBefore: number;
  remainingSecondaryDeductibleBefore: number;
}

export interface ApiCopayResponse {
  patientInfo: {
    balance: number;
    hasPaymentPlan: boolean;
    hasRentalSchedule: boolean;
  };
  insurances: {
    insuranceType: number;
  }[];
  items?: ApiCopayItemResponse[];
  copay?: {
    finalPatientAmount: number;
    items: ApiCopayDetailsItemResponse[];
  };
  collectionExcluded?: boolean;
  allowOverridePrimaryDeduct?: boolean;
  allowOverrideSecondaryDeduct?: boolean;
  primaryOutOfPocketMet?: boolean;
  secondaryOutOfPocketMet?: boolean;
}

// Front End Models

export interface PatientCopay {
  hasPaymentPlan: boolean;
  hasRentalSchedule: boolean;
  estimatedInsuranceCopayment: number;
  outstandingBalance: number;
  estimatedPatientResponsibility: number;
  // Computed fields
  excludeCopay: boolean;
  excludeBalance: boolean;
  excludeCopayAndBalance: boolean;
  allowOverridingPrimaryDeductible: boolean;
  allowOverridingSecondaryDeductible: boolean;
  primaryOutOfPocketMet: boolean;
  secondaryOutOfPocketMet: boolean;
  copayDetails: PatientCopayDetails | null;
}

export interface PatientCopayDetailsItem {
  description: string;
  quantity: number;
  details: { label: string; value: number }[];
  estimatedInsuranceCopayment: number;
}

export interface PatientCopayDetails {
  totalEstimatedInsuranceCopayment: number;
  items: PatientCopayDetailsItem[];
}

// Form Models

export interface DeductibleOverridesFormValues {
  overridePrimaryDeductible: boolean;
  overrideSecondaryDeductible: boolean;
}

// Mappers

export const mapCopay = (
  c: ApiCopayResponse,
  enableCopayExclusion: boolean,
  enableDeductibleOverrides: boolean
): PatientCopay => {
  const excludeCopay = enableCopayExclusion && !!c.collectionExcluded;
  const excludeBalance = c.patientInfo.hasPaymentPlan && !c.patientInfo.hasRentalSchedule;
  const excludeCopayAndBalance =
    (c.patientInfo.hasPaymentPlan && c.patientInfo.hasRentalSchedule) || (excludeCopay && excludeBalance);

  const outstandingBalance = Math.round(c.patientInfo.balance * 100) / 100;
  const estimatedInsuranceCopayment = Math.round((c.copay?.finalPatientAmount || 0) * 100) / 100;
  let estimatedPatientResponsibility = outstandingBalance + estimatedInsuranceCopayment;

  if (excludeCopayAndBalance) {
    estimatedPatientResponsibility = 0;
  } else if (excludeCopay) {
    estimatedPatientResponsibility = outstandingBalance;
  } else if (excludeBalance) {
    estimatedPatientResponsibility = estimatedInsuranceCopayment;
  }

  let copayDetails = null;

  if (c.copay && c.copay.items && c.items) {
    let items: PatientCopayDetailsItem[] = [];

    c.copay.items.forEach((i) => {
      const item = c.items?.find((it) => it.item.itemId === i.item.itemId);

      if (item) {
        let details = [
          {
            label: 'Estimated Insurance Allowed Amount',
            value: i.total,
          },
          {
            label: 'Remaining Primary Deductible',
            value: i.remainingDeductibleBefore,
          },
          {
            label: `Amount Covered by Primary Insurance (${Math.round(i.primaryReimbursementPercentage * 100) / 100}%)`,
            value: i.primaryInsuranceAmount,
          },
        ];

        const hasSecondaryInsurance = c.insurances.find((ins) => ins.insuranceType === 2);

        if (hasSecondaryInsurance) {
          details = [
            ...details,
            {
              label: 'Remaining Secondary Deductible',
              value: i.remainingSecondaryDeductibleBefore,
            },
            {
              label: `Amount Covered by Secondary Insurance (
                ${Math.round(i.secondaryReimbursementPercentage * 100) / 100}%)`,
              value: i.secondaryInsuranceAmount,
            },
          ];
        }

        items.push({
          description: item.item.description,
          quantity: item.item.quantity,
          estimatedInsuranceCopayment: i.patientAmount,
          details,
        });
      }
    });

    if (items.length === c.items?.length) {
      copayDetails = {
        items,
        totalEstimatedInsuranceCopayment: estimatedInsuranceCopayment,
      };
    }
  }

  return {
    hasPaymentPlan: c.patientInfo.hasPaymentPlan,
    hasRentalSchedule: c.patientInfo.hasRentalSchedule,
    estimatedInsuranceCopayment,
    outstandingBalance,
    estimatedPatientResponsibility,
    excludeCopay,
    excludeBalance,
    excludeCopayAndBalance,
    primaryOutOfPocketMet: !!c.primaryOutOfPocketMet,
    secondaryOutOfPocketMet: !!c.secondaryOutOfPocketMet,
    allowOverridingPrimaryDeductible: enableDeductibleOverrides && !!c.allowOverridePrimaryDeduct,
    allowOverridingSecondaryDeductible: enableDeductibleOverrides && !!c.allowOverrideSecondaryDeduct,
    copayDetails,
  };
};

export const buildCopayRequest = (
  patientId: number,
  reorderItems: ReorderDetailsItem[],
  datesOfService: DatesOfService,
  computedTargetShipDate: string,
  productUserData: ProductsUserData,
  useExclusionList: boolean,
  deductibleOverrides: DeductibleOverridesFormValues | null = null
): ApiCopayRequest => {
  return {
    patientId,
    deductibleMet: false,
    items: reorderItems.map((i) => {
      const itemDos = datesOfService.items.find((dos) => dos.uiId === i.uiId);
      const quantityToSubmit = productUserData.quantities?.find(
        (quantityItem) => quantityItem.itemUiId === i.uiId
      )?.quantityToSubmit;

      const itemQuantity = !!quantityToSubmit || quantityToSubmit === 0 ? quantityToSubmit : i.quantity!;

      return {
        id: i.id,
        cashSale: i.isCashSale,
        shipmentType: i.shipmentTypeId!,
        quantity: itemQuantity,
        shipmentDate: computedTargetShipDate,
        daysBetweenShipments: itemDos?.shippingFrequencyInMonths ? itemDos?.shippingFrequencyInMonths * 30 : 0, // TODO what if dos is not present?
        ship: true, // We never load non ship items in the portal
        charge: i.isChargeable,
        duplicate: i.isDuplicate,
      };
    }),
    useExclusionList: useExclusionList || undefined,
    overriddenPrimaryRemainingDeductible: deductibleOverrides?.overridePrimaryDeductible ? 0 : undefined,
    overriddenSecondaryRemainingDeductible: deductibleOverrides?.overrideSecondaryDeductible ? 0 : undefined,
  };
};
